import { useRef, useState } from "react";

/**
 * PatternLock — Android-style 9-dot pattern input.
 * onComplete(pattern) fires on pointer-up with a string like "0-1-2-5-8".
 * Patterns of < 4 dots are rejected silently (Android's minimum).
 */
const GRID = [0, 1, 2, 3, 4, 5, 6, 7, 8]; // indexes 0..8 (3×3)
const MIN_DOTS = 4;

export default function PatternLock({ size = 280, onComplete, error = false, disabled = false }) {
  const ref = useRef(null);
  const [path, setPath] = useState([]);      // indexes drawn so far
  const [cursor, setCursor] = useState(null); // current pointer {x,y} while dragging
  const [drawing, setDrawing] = useState(false);

  const step = size / 3;
  const dotRadius = Math.max(8, Math.floor(step / 10));

  const dotCenter = (i) => ({ x: (i % 3) * step + step / 2, y: Math.floor(i / 3) * step + step / 2 });

  const findDotAt = (x, y) => {
    for (const i of GRID) {
      const c = dotCenter(i);
      const dx = x - c.x, dy = y - c.y;
      if (dx * dx + dy * dy <= (dotRadius * 2.4) ** 2) return i;
    }
    return null;
  };

  const localCoords = (evt) => {
    const rect = ref.current.getBoundingClientRect();
    const touch = evt.touches?.[0] || evt.changedTouches?.[0] || evt;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const pointerDown = (evt) => {
    if (disabled) return;
    evt.preventDefault();
    setPath([]);
    setDrawing(true);
    const { x, y } = localCoords(evt);
    const hit = findDotAt(x, y);
    if (hit !== null) setPath([hit]);
    setCursor({ x, y });
  };

  const pointerMove = (evt) => {
    if (!drawing || disabled) return;
    evt.preventDefault();
    const { x, y } = localCoords(evt);
    setCursor({ x, y });
    const hit = findDotAt(x, y);
    if (hit !== null && !path.includes(hit)) {
      setPath((p) => [...p, hit]);
    }
  };

  const pointerUp = () => {
    if (!drawing) return;
    setDrawing(false);
    setCursor(null);
    if (path.length >= MIN_DOTS) {
      onComplete?.(path.join("-"));
    } else {
      // too short — clear after a beat so the user sees the reject animation
      setTimeout(() => setPath([]), 250);
    }
  };

  const strokeColor = error ? "#FF3B3B" : path.length >= MIN_DOTS ? "#22E07A" : "#F5D849";

  // Build polyline points (drawn dots) + trailing segment to cursor
  const pathPoints = path.map((i) => {
    const c = dotCenter(i);
    return `${c.x},${c.y}`;
  }).join(" ");
  const trailing = cursor && path.length > 0 ? (() => {
    const last = dotCenter(path[path.length - 1]);
    return `${last.x},${last.y} ${cursor.x},${cursor.y}`;
  })() : null;

  return (
    <div
      className="select-none"
      style={{ width: size, height: size, touchAction: "none" }}
      data-testid="pattern-lock"
      data-pattern-error={error ? "true" : "false"}
    >
      <svg
        ref={ref}
        width={size}
        height={size}
        onMouseDown={pointerDown}
        onMouseMove={pointerMove}
        onMouseUp={pointerUp}
        onMouseLeave={pointerUp}
        onTouchStart={pointerDown}
        onTouchMove={pointerMove}
        onTouchEnd={pointerUp}
        style={{ cursor: disabled ? "default" : "pointer" }}
      >
        {pathPoints && (
          <polyline
            points={pathPoints}
            fill="none"
            stroke={strokeColor}
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.9"
          />
        )}
        {trailing && (
          <polyline
            points={trailing}
            fill="none"
            stroke={strokeColor}
            strokeWidth="4"
            strokeLinecap="round"
            opacity="0.5"
          />
        )}
        {GRID.map((i) => {
          const c = dotCenter(i);
          const active = path.includes(i);
          return (
            <g key={i}>
              <circle
                cx={c.x}
                cy={c.y}
                r={dotRadius * 2.4}
                fill="transparent"
              />
              <circle
                cx={c.x}
                cy={c.y}
                r={active ? dotRadius * 1.6 : dotRadius}
                fill={active ? strokeColor : "#1B4238"}
                stroke={active ? strokeColor : "#2A5048"}
                strokeWidth="2"
                style={{ transition: "r 0.15s ease, fill 0.15s ease" }}
              />
              {active && (
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={dotRadius * 0.6}
                  fill="#051611"
                />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
